
import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Image,
  Text,
  Box,
  useColorModeValue,
} from '@chakra-ui/react'
import { MediaService } from '@/lib/services/mediaService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { Media, UpdateMediaData } from '@/types/media'

interface MediaEditModalProps {
  isOpen: boolean
  onClose: () => void
  media: Media
  onSuccess?: () => void
}

export function MediaEditModal({
  isOpen,
  onClose,
  media,
  onSuccess,
}: MediaEditModalProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<UpdateMediaData>({
    file_name: media.file_name,
    caption: media.caption || '',
    short_descriptions: media.short_descriptions || '',
  })

  useEffect(() => {
    if (media) {
      setFormData({
        file_name: media.file_name,
        caption: media.caption || '',
        short_descriptions: media.short_descriptions || '',
      })
    }
  }, [media])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await MediaService.updateMedia(media.id, formData)
      showSuccess('Media updated successfully')
      onSuccess?.()
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(media.file_url)
      showSuccess('URL copied to clipboard')
    } catch (error) {
      showError(new Error('Failed to copy URL'))
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Edit Media</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Preview */}
              <Box>
                {media.is_image && (
                  <Image
                    src={media.file_url}
                    alt={media.file_name}
                    maxH="300px"
                    objectFit="contain"
                    borderRadius="md"
                  />
                )}
                {media.is_video && (
                  <Box as="video" src={media.file_url} controls w="100%" borderRadius="md" />
                )}
                {media.is_audio && (
                  <Box as="audio" src={media.file_url} controls w="100%" />
                )}
                {!media.is_image && !media.is_video && !media.is_audio && (
                  <Box
                    p={8}
                    bg={colors.sectionBg}
                    border="1px"
                    borderColor={colors.border}
                    borderRadius="md"
                    textAlign="center"
                  >
                    <Text color={colors.textMuted}>Preview not available</Text>
                  </Box>
                )}
              </Box>

              {/* File Info */}
              <VStack spacing={2} align="stretch" fontSize="sm" color={colors.textMuted}>
                <HStack justify="space-between">
                  <Text>File Type:</Text>
                  <Text fontWeight="medium">{media.file_type}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>File Size:</Text>
                  <Text fontWeight="medium">
                    {media.formatted_file_size || `${media.file_size} B`}
                  </Text>
                </HStack>
                {media.file_dimensions && (
                  <HStack justify="space-between">
                    <Text>Dimensions:</Text>
                    <Text fontWeight="medium">{media.file_dimensions}</Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <Text>Uploaded:</Text>
                  <Text fontWeight="medium">{media.created_at}</Text>
                </HStack>
              </VStack>

              {/* Form Fields */}
              <FormControl>
                <FormLabel>File Name</FormLabel>
                <Input
                  value={formData.file_name}
                  onChange={(e) =>
                    setFormData({ ...formData, file_name: e.target.value })
                  }
                  placeholder="Enter file name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Caption</FormLabel>
                <Textarea
                  value={formData.caption || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, caption: e.target.value })
                  }
                  placeholder="Enter caption"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formData.short_descriptions || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, short_descriptions: e.target.value })
                  }
                  placeholder="Enter description"
                  rows={3}
                />
              </FormControl>

              {/* File URL */}
              <FormControl>
                <FormLabel>File URL</FormLabel>
                <HStack>
                  <Input value={media.file_url} isReadOnly fontSize="sm" />
                  <Button size="sm" onClick={handleCopyUrl}>
                    Copy
                  </Button>
                </HStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={isLoading}>
              Update
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

